import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, Eye, Loader2, LogIn, RefreshCw } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const PORTAL_HOSTS = new Set(["www.casadiriposoperte.it", "casadiriposoperte.it", "localhost", "127.0.0.1"]);
const FILTERS = [
  { id: "pending", label: "In attesa" },
  { id: "trial", label: "In prova" },
  { id: "expired", label: "Scadute" },
  { id: "tutte", label: "Tutte" },
];
const STAT_PERIODS = [
  { id: 7, label: "Ultimi 7 giorni" },
  { id: 30, label: "Ultimi 30 giorni" },
  { id: 90, label: "Ultimi 90 giorni" },
];
const STAT_LABELS = [
  ["visualizzazioni", "Visualizzazioni della scheda"],
  ["comparse_catalogo", "Comparse nelle ricerche"],
  ["comparse_matching", "Comparse nel matching"],
  ["richieste", "Richieste di informazioni"],
];

function periodDifference(now, previous) {
  const current = Number(now) || 0;
  const prior = Number(previous) || 0;
  if (!prior) return current ? "Nuovo dato" : "—";
  const value = Math.round(((current - prior) / prior) * 100);
  return `${value >= 0 ? "+" : ""}${value}% rispetto al periodo precedente`;
}

function countLabel(value, singular, plural) {
  const count = Number(value) || 0;
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatStatDay(iso) {
  if (!iso) return "";
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString("it-IT");
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT");
  } catch {
    return iso;
  }
}

function statusLabel(item) {
  if (item.approval_status === "pending") return "In attesa";
  if (item.approval_status === "rejected") return "Rifiutata";
  if (item.subscription_status === "trial") return "In prova";
  if (item.subscription_status === "active") return "Abbonamento attivo";
  if (item.subscription_status === "expired") return "Scaduta";
  return item.subscription_status || item.approval_status || "—";
}

function trialText(item) {
  if (!item) return "";
  const inTrial = item.subscription_status === "trial";
  const expiredUnpaid = item.subscription_status === "expired" && item.trial_days_remaining === 0 && !item.pagamento_effettuato;
  if (!inTrial && !expiredUnpaid) return "";
  const days = item.trial_days_remaining;
  if (days == null) return "In prova";
  if (days <= 0) return "Prova scaduta";
  const left = days === 1 ? "1 giorno rimasto" : `${days} giorni rimasti`;
  return item.trial_end ? `${left} · fino al ${formatWhen(item.trial_end)}` : left;
}

function paymentBadgeClass(stato) {
  if (stato === "paid") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (stato === "pending" || stato === "processing") return "border-amber-200 bg-amber-50 text-amber-800";
  if (stato === "nessuno") return "border-slate-200 bg-slate-50 text-slate-600";
  return "border-rose-200 bg-rose-50 text-rose-800";
}

function trialDialogText(item) {
  const live = trialText(item);
  if (live) return live;
  if (item?.subscription_status === "active" || item?.pagamento_effettuato || item?.trial_end) return "Prova conclusa";
  return "Prova non avviata";
}

function paymentSummary(item) {
  const label = item.pagamento_stato_label || "Nessun pagamento";
  if (item.pagamento_effettuato && item.subscription_plan_label) {
    return `Abbonamento: ${label} · ${item.subscription_plan_label}`;
  }
  return `Abbonamento: ${label}`;
}

const SCHEDA_GROUPS = [
  {
    title: "Struttura",
    fields: [
      ["nome_struttura", "Nome"],
      ["tipo_struttura", "Tipo"],
      ["email_struttura", "Email"],
      ["sito_web", "Sito web"],
      ["facebook", "Facebook"],
    ],
  },
  {
    title: "Indirizzo",
    fields: [
      ["via", "Via"],
      ["numero_civico", "Civico"],
      ["comune", "Comune"],
      ["provincia", "Provincia"],
    ],
  },
  {
    title: "Referente",
    fields: [
      ["nome_referente", "Nome"],
      ["telefono_referente", "Telefono"],
    ],
  },
  {
    title: "Capacità e rette",
    fields: [
      ["posti_totali", "Posti totali"],
      ["posti_diurni", "Posti diurni"],
      ["prezzo_singola_wc", "Singola con bagno", "euro"],
      ["prezzo_doppia_wc", "Doppia con bagno", "euro"],
      ["prezzo_singola_no_wc", "Singola senza bagno", "euro"],
      ["prezzo_doppia_no_wc", "Doppia senza bagno", "euro"],
    ],
  },
  {
    title: "Servizi",
    fields: [
      ["presenza_del_medico", "Presenza del medico", "bool"],
      ["infermieri", "Infermieri", "bool"],
      ["giardino_spazi_esterni_fruibili", "Giardino e spazi esterni", "bool"],
      ["attivita_occupazionali", "Attività occupazionali", "bool"],
      ["note_attivita", "Note attività"],
    ],
  },
];

const euroFormat = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

function fieldFilled(value, kind) {
  if (kind === "bool") return value === true;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return !Number.isNaN(value);
  return Boolean(value);
}

function formatField(value, kind) {
  if (!fieldFilled(value, kind)) return kind === "bool" ? "Non indicato" : "Non compilato";
  if (kind === "bool") return "Sì";
  if (kind === "euro") return euroFormat.format(Number(value));
  return String(value);
}

function schedaStats(scheda) {
  const fields = SCHEDA_GROUPS.flatMap((group) => group.fields);
  const filled = fields.filter(([key, , kind]) => fieldFilled(scheda?.[key], kind)).length;
  const images = Number(scheda?.immagini) || 0;
  const total = fields.length + 1;
  return { filled: filled + (images > 0 ? 1 : 0), total, images };
}

export default function RegistrazioniPortaleAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [stato, setStato] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (nextStato = stato) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/admin/casadiriposoperte/registrazioni?stato=${encodeURIComponent(nextStato)}`,
        { headers }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento registrazioni");
      setItems(json.registrazioni || []);
    } catch (error) {
      toast.error(error.message || "Errore registrazioni");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [stato, headers]);

  useEffect(() => {
    load(stato);
  }, [stato]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(id, action) {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/registrazioni/${id}/${action}`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Operazione non riuscita");
      toast.success(json.message || "Aggiornata");
      await load(stato);
    } catch (error) {
      toast.error(error.message || "Operazione non riuscita");
    } finally {
      setBusyId("");
    }
  }

  async function enterPortal(item) {
    const popup = window.open("", "_blank");
    setBusyId(item.id);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/registrazioni/${item.id}/entra`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Accesso al portale non riuscito");
      const url = new URL(json.url);
      const path = url.pathname.replace(/\/+$/, "");
      if (!PORTAL_HOSTS.has(url.hostname) || !path.endsWith("/entra")) {
        throw new Error("Indirizzo di accesso non valido");
      }
      if (popup) {
        popup.location.href = url.toString();
      } else if (!window.open(url.toString(), "_blank")) {
        throw new Error("Il browser ha bloccato la finestra. Consenti i popup per questo sito e riprova.");
      }
    } catch (error) {
      if (popup) popup.close();
      toast.error(error.message || "Accesso al portale non riuscito");
    } finally {
      setBusyId("");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-rose-600" />
            Registrazioni portale
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Account creati su casadiriposoperte.it. Attivando una registrazione partono i 14 giorni di prova,
            la scheda entra nel catalogo e la struttura riceve l&apos;email di attivazione.
            Alla scadenza esce dal catalogo finché non paga l&apos;abbonamento.
            Ogni riga riporta le statistiche degli ultimi 30 giorni, le stesse dell&apos;area strutture.
            Entra apre il sito già dentro quell&apos;account: il link vale poco più di un minuto e si usa una volta sola.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(stato)} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((filter) => (
          <Button
            key={filter.id}
            variant={stato === filter.id ? "default" : "outline"}
            onClick={() => setStato(filter.id)}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento
            </div>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">Nessuna registrazione in questo elenco.</p>
          ) : (
            <div className="divide-y">
              {items.map((item) => {
                const stats = schedaStats(item.scheda);
                return (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{item.nome_struttura}</p>
                      <p className="text-sm text-slate-500">
                        {item.email}
                        {item.tipo_struttura ? ` · ${item.tipo_struttura}` : ""}
                        {item.comune ? ` · ${item.comune}${item.provincia ? ` (${item.provincia})` : ""}` : ""}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatWhen(item.created_at)}
                        {item.registration_source ? ` · ${item.registration_source}` : ""}
                        {` · ${stats.filled}/${stats.total} campi compilati`}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        Ultimi 30 giorni: {countLabel(item.statistiche?.visualizzazioni, "visualizzazione", "visualizzazioni")}
                        {" · "}
                        {countLabel(item.statistiche?.comparse_catalogo, "comparsa in ricerca", "comparse in ricerca")}
                        {" · "}
                        {countLabel(item.statistiche?.comparse_matching, "comparsa nel matching", "comparse nel matching")}
                        {" · "}
                        {countLabel(item.statistiche?.richieste, "richiesta", "richieste")}
                      </p>
                      {trialText(item) ? (
                        <p className="text-xs font-medium text-sky-800 mt-1">{trialText(item)}</p>
                      ) : null}
                      {(item.pagamenti || []).length > 0 ? (
                        <p className="text-xs text-slate-500 mt-1">
                          Tentativi: {item.pagamenti.map((payment) => payment.stato_label).join(" · ")}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{statusLabel(item)}</Badge>
                      <Badge variant="outline" className={paymentBadgeClass(item.pagamento_stato)}>
                        {paymentSummary(item)}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => setSelected(item)}>
                        <Eye className="w-4 h-4 mr-1.5" />
                        Dettagli
                      </Button>
                      <Button size="sm" variant="outline" disabled={busyId === item.id} onClick={() => enterPortal(item)}>
                        <LogIn className="w-4 h-4 mr-1.5" />
                        Entra
                      </Button>
                      {item.approval_status === "pending" ? (
                        <>
                          <Button size="sm" disabled={busyId === item.id} onClick={() => act(item.id, "attiva")}>
                            Attiva prova
                          </Button>
                          <Button size="sm" variant="outline" disabled={busyId === item.id} onClick={() => act(item.id, "rifiuta")}>
                            Rifiuta
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <SchedaDialog
        item={selected}
        headers={headers}
        busy={busyId === selected?.id}
        onEnter={() => selected && enterPortal(selected)}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function SchedaDialog({ item, headers, busy, onEnter, onClose }) {
  const scheda = item?.scheda || {};
  const stats = schedaStats(scheda);
  const [days, setDays] = useState(30);
  const [portalStats, setPortalStats] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);
  const unavailable = item?.approval_status === "pending" || item?.subscription_status === "expired";
  const peak = Math.max(1, ...(portalStats?.serie || []).map((point) => Number(point.visualizzazioni) || 0));

  useEffect(() => {
    if (!item?.id) return undefined;
    let cancelled = false;
    setPortalStats(null);
    setStatsError("");
    setStatsLoading(true);
    fetch(
      `${API}/api/admin/casadiriposoperte/registrazioni/${item.id}/statistiche?giorni=${days}`,
      { headers }
    )
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.detail || "Statistiche non disponibili");
        if (!cancelled) setPortalStats(json);
      })
      .catch((error) => {
        if (!cancelled) setStatsError(error.message || "Statistiche non disponibili");
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item?.id, days, headers]);

  return (
    <Dialog open={!!item} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item?.nome_struttura || "Scheda struttura"}</DialogTitle>
          <p className="text-sm text-slate-500">
            {stats.filled} di {stats.total} campi compilati
            {item?.email ? ` · ${item.email}` : ""}
          </p>
        </DialogHeader>
        <div className="space-y-5">
          <section>
            <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Statistiche</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Come la struttura viene scoperta su CasaDiRiposoPerTe. Gli stessi numeri dell&apos;area strutture.
                </p>
              </div>
              <label className="text-sm font-medium text-slate-700">
                Periodo
                <select
                  value={days}
                  onChange={(event) => setDays(Number(event.target.value))}
                  className="ml-2 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                >
                  {STAT_PERIODS.map((period) => (
                    <option key={period.id} value={period.id}>{period.label}</option>
                  ))}
                </select>
              </label>
            </div>
            {unavailable ? (
              <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Le statistiche inizieranno a essere raccolte quando la scheda sarà attiva nel catalogo.
              </p>
            ) : null}
            {statsError ? (
              <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{statsError}</p>
            ) : null}
            {statsLoading && !portalStats ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Caricamento statistiche
              </p>
            ) : null}
            {portalStats ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {STAT_LABELS.map(([key, label]) => (
                    <div key={key} className="rounded-lg bg-slate-50 p-3">
                      <p className="text-xs font-medium text-slate-500">{label}</p>
                      <p className="mt-1 text-2xl font-semibold text-slate-900">{portalStats.totali?.[key] || 0}</p>
                      <p className="mt-1 text-xs font-medium text-slate-600">
                        {periodDifference(portalStats.totali?.[key], portalStats.precedente?.[key])}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 rounded-lg bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">Visualizzazioni giornaliere</p>
                  <div className="mt-3 flex h-28 items-end gap-px" aria-label="Grafico visualizzazioni giornaliere">
                    {(portalStats.serie || []).map((point) => {
                      const views = Number(point.visualizzazioni) || 0;
                      return (
                        <span key={point.giorno} className="flex h-full flex-1 items-end" title={`${formatStatDay(point.giorno)}: ${views}`}>
                          <span
                            className="w-full rounded-t bg-sky-800"
                            style={{ height: `${Math.max(views ? 6 : 2, (views / peak) * 100)}%` }}
                          />
                        </span>
                      );
                    })}
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {portalStats.raccolta_da
                    ? `Dati raccolti dal ${formatStatDay(portalStats.raccolta_da)}.`
                    : "La raccolta dati è appena iniziata: i valori appariranno quando la scheda riceverà visite."}
                </p>
              </>
            ) : null}
          </section>
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Prova e abbonamento</h3>
            <p className="text-sm text-slate-900">{trialDialogText(item)}</p>
            <p className="mt-1 text-sm text-slate-900">
              Pagamento abbonamento: {item?.pagamento_stato_label || "Nessun pagamento"}
              {item?.subscription_plan_label ? ` · ${item.subscription_plan_label}` : ""}
              {item?.subscription_end && item?.pagamento_effettuato ? ` · fino al ${formatWhen(item.subscription_end)}` : ""}
            </p>
            {Array.isArray(item?.pagamenti) && item.pagamenti.length > 0 ? (
              <ul className="mt-2 space-y-1">
                {item.pagamenti.map((payment, index) => (
                  <li key={`${payment.created_at}-${payment.stato}-${index}`} className="text-sm text-slate-700">
                    {formatWhen(payment.created_at)}
                    {payment.piano_label ? ` · ${payment.piano_label}` : ""}
                    {payment.importo != null && payment.importo !== "" ? ` · ${euroFormat.format(Number(payment.importo))}` : ""}
                    {` · ${payment.stato_label}`}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm italic text-slate-400">Nessun tentativo di pagamento dell&apos;abbonamento</p>
            )}
          </section>
          {SCHEDA_GROUPS.map((group) => (
            <section key={group.title}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{group.title}</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.fields.map(([key, label, kind]) => {
                  const filled = fieldFilled(scheda[key], kind);
                  return (
                    <div key={key} className={key === "note_attivita" ? "sm:col-span-2" : ""}>
                      <p className="text-xs font-medium text-slate-500">{label}</p>
                      <p className={`mt-0.5 text-sm ${filled ? "text-slate-900" : "italic text-slate-400"}`}>
                        {formatField(scheda[key], kind)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Immagini</h3>
            {Array.isArray(scheda.foto) && scheda.foto.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {scheda.foto.map((photo) => (
                  <a
                    key={photo.url}
                    href={photo.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                  >
                    <img
                      src={photo.url}
                      alt={photo.didascalia || "Foto della struttura"}
                      className="h-32 w-full object-cover"
                    />
                    {photo.didascalia ? (
                      <p className="truncate px-2 py-1 text-xs text-slate-500">{photo.didascalia}</p>
                    ) : null}
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm italic text-slate-400">Nessuna immagine</p>
            )}
          </section>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onEnter}>
            <LogIn className="w-4 h-4 mr-1.5" />
            Entra nel portale
          </Button>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
